import React from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MobileSpacing } from './shared/MobileResponsiveSpacing';
import { MENU_CONFIG } from './shared/MobileSettingsConstants';
import { MOBILE_SETTINGS_STYLES } from './shared/MobileSettingsStyles';
import { useMobileSettingsNavigation } from './shared/hooks/useMobileSettingsNavigation';
import { useSettingsMenuActions } from './shared/hooks/useSettingsMenuActions';
import usePasskeyManagementAccess from 'app/auth/usePasskeyManagementAccess';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType;
  path?: string;
  action?: () => void;
}

// Reusable MenuItemButton component
const MenuItemButton = ({ item, onClick }: { item: MenuItem; onClick: () => void }) => (
  <ListItemButton onClick={onClick} sx={MOBILE_SETTINGS_STYLES.listItem}>
    <ListItemIcon sx={MOBILE_SETTINGS_STYLES.listItemIcon}>
      <item.icon />
    </ListItemIcon>
    <ListItemText primary={item.label} primaryTypographyProps={MOBILE_SETTINGS_STYLES.listItemText} />
  </ListItemButton>
);

function MobileSettingsMenu(): JSX.Element {
  const { t } = useTranslation();
  const { handleBackToHome } = useMobileSettingsNavigation();
  const { handleSignOut, navigate } = useSettingsMenuActions();

  // SEE-226: federated sessions can't hold passkeys, so drop the entry entirely
  // rather than lead them to an empty screen.
  const { canManagePasskeys } = usePasskeyManagementAccess();

  // Menu configuration for better maintainability
  const menuConfig = MENU_CONFIG.filter((config) => config.id !== 'security' || canManagePasskeys).map(
    (config) => {
      const baseItem = {
        id: config.id,
        label: t(config.labelKey),
        icon: config.icon,
      };

      if ('path' in config) {
        return { ...baseItem, path: config.path };
      } else {
        return { ...baseItem, action: handleSignOut };
      }
    }
  );

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <MobilePageContent
      transparentHeight={BACKGROUND_SIZES_v2.SHOW_HEADER}
      title={t('mobileMenuTitle')}
      showCompanySwitcher
      sx={{ p: 4 }}
    >
      {/* Menu Items List */}
      <Box
        sx={{
          flex: 1,
          pt: 2, // Add top padding for menu list
          pb: MobileSpacing.containerPaddingY, // Only bottom padding, no top padding
        }}
      >
        <Box sx={MOBILE_SETTINGS_STYLES.menuBox}>
          <List sx={MOBILE_SETTINGS_STYLES.listContainer}>
            {menuConfig.map((item, index) => (
              <React.Fragment key={item.id}>
                <MenuItemButton item={item} onClick={() => handleMenuItemClick(item)} />
                {index < menuConfig.length - 1 && (
                  <Divider
                    sx={{
                      borderColor: 'grey.200',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Box>
    </MobilePageContent>
  );
}

export default MobileSettingsMenu;
